import { concat } from '@ember/helper';
import { or } from 'tio-ui/utilities';
import { t } from 'ember-intl';
import FileUploader from '../../../file-uploader.gts';
import {
  AI_READABLE_FILE_ACCEPT,
  AI_READABLE_FILE_TYPES_DISPLAY,
  TA_MAX_UPLOAD_FILE_SIZE_BYTES,
} from '../../../../utils/tuition-assistance/ai-validation.ts';
import type { TOC } from '@ember/component/template-only';
import { PhCloudArrowUp } from 'ember-phosphor-icons';

const TA_MAX_UPLOAD_FILE_SIZE_MB = TA_MAX_UPLOAD_FILE_SIZE_BYTES / (1024 * 1024);

interface UploadAttachmentsUploaderSignature {
  Args: {
    didUploadFile: (content: unknown) => void;
    isProcessing: boolean;
    disabled?: boolean;
    useDropzone?: boolean;
    iconOnlyUpload?: boolean;
  };
}

const TuitionAssistanceFormsUploadAttachmentsUploaderComponent: TOC<UploadAttachmentsUploaderSignature> =
  <template>
    {{#if @useDropzone}}
      <FileUploader
        @didUploadFile={{@didUploadFile}}
        @disabled={{@disabled}}
        @useDropzone={{@useDropzone}}
        @accept={{AI_READABLE_FILE_ACCEPT}}
        @enforceAccept={{true}}
        @maxFileSize={{TA_MAX_UPLOAD_FILE_SIZE_BYTES}}
      />
    {{else if @iconOnlyUpload}}
      <FileUploader
        @didUploadFile={{@didUploadFile}}
        @accept={{AI_READABLE_FILE_ACCEPT}}
        @enforceAccept={{true}}
        @maxFileSize={{TA_MAX_UPLOAD_FILE_SIZE_BYTES}}
        as |uploader|
      >
        <input
          id={{uploader.uniqueId}}
          type="file"
          multiple="true"
          accept={{AI_READABLE_FILE_ACCEPT}}
          {{uploader.queue.selectFile}}
          class="hidden"
          disabled={{or @disabled uploader.queue.files.length @isProcessing}}
        />
        {{! Icon-only uploads expose type and size limits through the accessible name and tooltip. }}
        <label
          for={{uploader.uniqueId}}
          class="cursor-pointer text-center font-semibold uppercase tio-flex-center items-start relative overflow-hidden px-3
            {{if @disabled 'tio-fg-disabled' 'tio-fg-info hover:tio-fg-info-h'}}"
          aria-label={{concat
            (t "common.choose_file")
            " — "
            AI_READABLE_FILE_TYPES_DISPLAY
            " · "
            (t "file_upload.max_file_size" maxSize=TA_MAX_UPLOAD_FILE_SIZE_MB)
          }}
          title={{concat
            AI_READABLE_FILE_TYPES_DISPLAY
            " · "
            (t "file_upload.max_file_size" maxSize=TA_MAX_UPLOAD_FILE_SIZE_MB)
          }}
          data-test-upload-attachments-icon-constraints
        >

          <PhCloudArrowUp @size="32" class="mb-2 tio-fg-info" />
        </label>
      </FileUploader>
    {{else}}
      <FileUploader
        @didUploadFile={{@didUploadFile}}
        @accept={{AI_READABLE_FILE_ACCEPT}}
        @enforceAccept={{true}}
        @maxFileSize={{TA_MAX_UPLOAD_FILE_SIZE_BYTES}}
        as |uploader|
      >
        <input
          id={{uploader.uniqueId}}
          type="file"
          multiple="true"
          accept={{AI_READABLE_FILE_ACCEPT}}
          {{uploader.queue.selectFile}}
          class="hidden"
          disabled={{or @disabled uploader.queue.files.length @isProcessing}}
        />
        <label
          for={{uploader.uniqueId}}
          class="cursor-pointer text-center font-semibold uppercase tio-row-center relative overflow-hidden px-3 py-2
            {{unless @iconOnlyUpload 'border max-w-48 my-6 tio-bg-surface'}}
            {{if
              @disabled
              'tio-border-default tio-fg-disabled'
              'tio-border-info hover:tio-border-info tio-fg-info hover:tio-fg-info-h'
            }}"
        >
          {{t "common.choose_file"}}
          {{#if (or uploader.queue.files.length @isProcessing)}}
            <div
              class="tio-row-center absolute inset-0 h-full w-full cursor-default tio-bg-surface tio-fg-info"
              aria-hidden="true"
            >
            </div>
          {{/if}}
        </label>
        <ul class="text-xs list-disc pl-2">
          <li>
            {{t "tuition_assistance.program_details.courses.you_can_upload_multiple_attachments"}}
          </li>
          <li data-test-upload-attachments-constraints>
            {{AI_READABLE_FILE_TYPES_DISPLAY}}
            ·
            {{t
              "file_upload.max_file_size"
              maxSize=TA_MAX_UPLOAD_FILE_SIZE_MB
              default="Max file size {maxSize}MB"
            }}
          </li>
        </ul>
      </FileUploader>
    {{/if}}
  </template>;

export default TuitionAssistanceFormsUploadAttachmentsUploaderComponent;
